import * as Yup from 'yup'
import { DocumentoPessoaLabels, DocumentoPessoaType } from '_metronic/helpers'
import { IOption } from 'app/components'
import { TFunction } from '_metronic/i18n/Metronici18n'
import { DocumentoPessoa, TIPOS_DOCUMENTO_ESTRANGEIRO } from './_models'

/**
 * Identificadores do formulário principal (SBIS ECF.02.01): CPF e CNS independentes e, para quem não tem
 * nenhum dos dois, um documento estrangeiro (passaporte ou BI). Mantém a regra de antes do seletor
 * exclusivo — pelo menos um identificador — sem endurecê-la.
 */
export type IdentificadoresValues = {
  cpf: string
  cns: string
  documentoEstrangeiroTipo?: IOption<DocumentoPessoaType>
  documentoEstrangeiroNumero: string
}

export const OPCOES_DOCUMENTO_ESTRANGEIRO: IOption<DocumentoPessoaType>[] =
  TIPOS_DOCUMENTO_ESTRANGEIRO.map((tipo) => ({ label: DocumentoPessoaLabels[tipo], value: tipo }))

/** A pessoa já tem passaporte ou BI na lista de documentos (só existe em edição). */
export const temDocumentoEstrangeiro = (documentos?: { tipo: DocumentoPessoaType }[]) =>
  (documentos || []).some((doc) => TIPOS_DOCUMENTO_ESTRANGEIRO.includes(doc.tipo))

/** O campo de documento estrangeiro só aparece quando nada mais identifica a pessoa. */
export const precisaDocumentoEstrangeiro = (
  cpf: string | undefined,
  cns: string | undefined,
  documentoEstrangeiroNaLista: boolean
) => !cpf && !cns && !documentoEstrangeiroNaLista

export const valoresIniciaisIdentificadores = (
  cpf?: string | null,
  cns?: string | null,
  documentoInicial?: { tipo?: DocumentoPessoaType; numero?: string }
): IdentificadoresValues => {
  const estrangeiro =
    documentoInicial?.tipo && TIPOS_DOCUMENTO_ESTRANGEIRO.includes(documentoInicial.tipo)
      ? documentoInicial
      : undefined
  return {
    cpf:
      cpf ||
      (documentoInicial?.tipo === DocumentoPessoaType.Cpf ? documentoInicial.numero : '') ||
      '',
    cns:
      cns ||
      (documentoInicial?.tipo === DocumentoPessoaType.Cns ? documentoInicial.numero : '') ||
      '',
    documentoEstrangeiroTipo: estrangeiro?.tipo
      ? { label: DocumentoPessoaLabels[estrangeiro.tipo], value: estrangeiro.tipo }
      : OPCOES_DOCUMENTO_ESTRANGEIRO[0],
    documentoEstrangeiroNumero: estrangeiro?.numero || '',
  }
}

/** Campos `passaporte`/`bilheteIdentidadeAngola` do payload, que a API já aceita no create e no update. */
export const dtoDocumentoEstrangeiro = (
  values: IdentificadoresValues,
  documentoEstrangeiroNaLista: boolean
) => {
  if (!precisaDocumentoEstrangeiro(values.cpf, values.cns, documentoEstrangeiroNaLista)) {
    return { passaporte: undefined, bilheteIdentidadeAngola: undefined }
  }
  const numero = values.documentoEstrangeiroNumero.trim() || undefined
  const tipo = values.documentoEstrangeiroTipo?.value
  return {
    passaporte: tipo === DocumentoPessoaType.Passaporte ? numero : undefined,
    bilheteIdentidadeAngola:
      tipo === DocumentoPessoaType.BilheteIdentidadeAngola ? numero : undefined,
  }
}

/** Regras Yup dos identificadores, para espalhar dentro do `shape` do formulário principal. */
export const identificadoresShape = (t: TFunction, documentoEstrangeiroNaLista: boolean) => ({
  cpf: Yup.string()
    .notRequired()
    .test(
      'cpf-formato',
      'CPF inválido',
      (valor) => !valor || /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(valor)
    ),
  cns: Yup.string().notRequired(),
  documentoEstrangeiroTipo: Yup.object().nullable(),
  documentoEstrangeiroNumero: Yup.string().when(['cpf', 'cns'], {
    is: (cpf?: string, cns?: string) =>
      precisaDocumentoEstrangeiro(cpf, cns, documentoEstrangeiroNaLista),
    then: (schema) =>
      schema.trim().max(30).required(t('documento_pessoa.identificador_obrigatorio')),
    otherwise: (schema) => schema.notRequired(),
  }),
})

/**
 * Documento "em uso" para o e-SUS (`pessoa.cpfOuCns`): CNS só quando não há CPF. Substitui o valor do
 * antigo seletor exclusivo.
 */
export const tipoDocumentoEmUso = (values?: { cpf?: string; cns?: string }) =>
  !values?.cpf && values?.cns ? DocumentoPessoaType.Cns : DocumentoPessoaType.Cpf

export type { DocumentoPessoa }
